import cn from "classnames";
import Image from "next/image";
import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";

import { BaseButton } from "@/src/component/base/BaseButton";
import CarouselImage from "@/src/component/partial/CarouselImage/CarouselImage";
import { DEEPKSY_IMGIX } from "@/src/const";
import { DeepSkyType, StrapiComponentType } from "@/src/type";

import theme from "@/styles/scss/theme.module.scss";
import styles from "./ImageGrid.module.scss";
import { LoadFromCDN } from "@/src/util";

const changeWidthParam = (url: string, size?: string) => {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete("w");
    urlObj.searchParams.set("w", size ?? "800");

    urlObj.searchParams.delete("width");
    urlObj.searchParams.set("width", size ?? "800");
    return urlObj.toString();
  } catch (err) {}
  return url;
};

interface ImageGridProps {
  posterUrl?: string;
  floorplan: DeepSkyType.AssetType.PropertyInventory.MarketingFloorPlan;
  unitCarousel?: StrapiComponentType.Object.AssetCaption[];
  title?: string;
  mobileHint?: boolean;
  deviceWidthHint?: number;
  quality?: number;
}

function ImageGrid({ floorplan, unitCarousel, title, posterUrl, mobileHint, deviceWidthHint, quality }: ImageGridProps) {
  const imageSize = mobileHint ? deviceWidthHint : 1920;
  const [openImageDialog, setOpenImageDialog] = useState<boolean | number>(false);
  const floorplanImage = posterUrl ?? floorplan?.media?.[0]?.external?.sourceUrl ?? `${DEEPKSY_IMGIX}/${floorplan.media?.[0]?.fid}`;
  const transformedUnitCarousel = (unitCarousel ?? []).map((item, index) => ({
    url: LoadFromCDN.imageQualityAndSizeOptional(item.asset.data.attributes.url, quality, imageSize),
    alt: item.name ?? `${title ?? "Image"} ${index + 1}`,
    id: `${item.asset.data.id}-${index}`,
  }));
  const firstImage = {
    url: LoadFromCDN.imageSizeAndQuality(floorplanImage, quality, imageSize),
    alt: "Floorplan Image",
    id: "Floorplan Image",
  };
  const carouselImages = [firstImage, ...transformedUnitCarousel];

  /**
   * close the image gallery
   */
  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
  };

  /**
   * open the image gallery
   */
  const handleOpenImageDialog = (toIndex: number) => {
    setOpenImageDialog(toIndex);
  };

  return (
    <>
      <div className={styles.container}>
        <div
          className={cn(styles.image, styles.a)}
          onClick={() => handleOpenImageDialog(0)}
        >
          <Image
            src={changeWidthParam(carouselImages?.[0]?.url, "600")}
            overrideSrc={changeWidthParam(carouselImages?.[0]?.url, "600")}
            loader={LoadFromCDN.loaderForNextJS}
            width={660}
            height={660}
            quality={quality}
            alt={carouselImages?.[1]?.alt}
            priority={!mobileHint}
            style={{
              objectFit: "contain",
            }}
          />
        </div>
        <div
          className={cn(styles.image, styles.b)}
          onClick={() => handleOpenImageDialog(1)}
        >
          <Image
            src={changeWidthParam(carouselImages?.[1]?.url, "480")}
            overrideSrc={changeWidthParam(carouselImages?.[1]?.url, "480")}
            loader={LoadFromCDN.loaderForNextJS}
            width={400}
            height={400}
            quality={quality}
            alt={carouselImages?.[1]?.alt}
            priority={!mobileHint}
          />
        </div>
        <div
          className={cn(styles.image, styles.c)}
          onClick={() => handleOpenImageDialog(2)}
        >
          <Image
            src={changeWidthParam(carouselImages?.[2]?.url, "480")}
            overrideSrc={changeWidthParam(carouselImages?.[2]?.url, "480")}
            loader={LoadFromCDN.loaderForNextJS}
            width={400}
            height={400}
            quality={quality}
            alt={carouselImages?.[2]?.alt}
            priority={!mobileHint}
          />
        </div>
        <div
          className={cn(styles.image, styles.d)}
          onClick={() => handleOpenImageDialog(3)}
        >
          <Image
            src={changeWidthParam(carouselImages?.[3]?.url, "480")}
            overrideSrc={changeWidthParam(carouselImages?.[3]?.url, "480")}
            loader={LoadFromCDN.loaderForNextJS}
            width={400}
            height={400}
            quality={quality}
            alt={carouselImages?.[3]?.alt}
            priority={!mobileHint}
          />
        </div>
        <div
          className={cn(styles.image, styles.e)}
          onClick={() => handleOpenImageDialog(4)}
        >
          <Image
            src={changeWidthParam(carouselImages?.[4]?.url, "480")}
            overrideSrc={changeWidthParam(carouselImages?.[4]?.url, "480")}
            loader={LoadFromCDN.loaderForNextJS}
            width={400}
            height={400}
            quality={quality}
            alt={carouselImages?.[4]?.alt}
            priority={!mobileHint}
          />
        </div>
        <div className={styles.allPhotoesCta}>
          <BaseButton
            variant="outlined"
            className={styles.allPhotoesCtaButton}
            onClick={() => handleOpenImageDialog(0)}
          >
            See all {(carouselImages ?? []).length} photos
          </BaseButton>
        </div>
      </div>
      {typeof openImageDialog === "number" && (
        <Dialog
          onClose={handleCloseImageDialog}
          open={typeof openImageDialog === "number"}
        >
          <div className={styles.imageDialogContainer}>
            <IconButton
              className={styles.imageDialogExit}
              onClick={handleCloseImageDialog}
            >
              <CloseIcon
                fontSize="large"
                sx={{ color: theme.secondaryMain }}
              />
            </IconButton>
            <div className={styles.imageDialogCarousel}>
              <CarouselImage
                index={typeof openImageDialog === "number" ? openImageDialog : 0}
                images={carouselImages}
                autoPlay={false}
                imageClassName={styles.imageDialogImage}
                navButtonsAlwaysVisible={true}
                animation="slide"
                objectFit="contain"
                bgResponsive={true}
              />
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

export default ImageGrid;
